import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material';
import { IconScale, IconTrophy } from '@tabler/icons-react';

import {
  Button,
  Chip,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import LoadingCard from './LoadingCard';
import { getWorkflowBuilderUrl } from 'src/pages/intent-conversation-analytics/helpers';
import { formatNumberWithCommas } from 'src/pages/workflow-builder/intent-workflows-table/utils';
import { PublishVersionDialog } from 'src/pages/workflow-builder-edit/autonomous-agent/new-version-history/PublishVersionDialog';
import { useVersionHistory } from 'src/pages/workflow-builder-edit/autonomous-agent/new-version-history/useVersionHistory';
import {
  useGetAbTestConfigByIdQuery,
  useGetWinnerQuery,
  useListAbTestConfigsSummaryQuery,
} from 'src/services/agent-ab-test/agentAbTestApi';
import { VARIANT_DEFAULT_NAMES } from 'src/services/agent-ab-test/types';

export default function WinnerCard() {
  const theme = useTheme();
  const { abTestId = '' } = useParams();
  const { t } = useTranslation('agent-ab-test');

  const { data: abTestConfig } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const { versionHistory } = useVersionHistory({
    intentWorkflowId: abTestConfig?.intent_workflow_id || '',
    skip: !abTestConfig?.intent_workflow_id,
  });

  const {
    data: abTestConfigsSummary,
    isFetching: isFetchingAbTestConfigsSummary,
  } = useListAbTestConfigsSummaryQuery({
    // TODO: implement pagination
    page: 1,
    pageSize: 5000,
  });

  const { data: winnerData, isFetching: isWinnerFetching } = useGetWinnerQuery(
    abTestId,
    {
      skip:
        !abTestId || !abTestConfig?.status || abTestConfig?.status === 'draft',
    },
  );

  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);

  const abTestConfigSummary = abTestConfigsSummary?.ab_test_configs.find(
    config => config.ab_test_config_id === abTestId,
  );

  const winnerVersion = winnerData?.winner
    ? versionHistory?.find(
        v => v.version === winnerData.winner?.intent_workflow_version,
      )
    : undefined;

  // Check if the winner is already published or a different version has been published
  const liveVersion = versionHistory?.find(v => v.is_published);
  const isWinnerAlreadyPublished = !!(
    winnerVersion &&
    liveVersion &&
    liveVersion.version === winnerVersion.version
  );
  const isDifferentVersionPublished = !!(
    winnerVersion &&
    liveVersion &&
    liveVersion.version !== winnerVersion.version
  );
  const isPublishDisabled =
    !winnerVersion || isWinnerAlreadyPublished || isDifferentVersionPublished;

  const publishDisabledTooltip = isWinnerAlreadyPublished
    ? t('winnerCard.publishedTooltip')
    : isDifferentVersionPublished
      ? t('winnerCard.differentVersionTooltip')
      : !winnerVersion
        ? t('winnerCard.versionNotFoundTooltip')
        : '';

  const getVariantEditUrl = (variantVersion: number, versionType: string) =>
    abTestConfig?.intent_workflow_id
      ? getWorkflowBuilderUrl({
          intentWorkflowId: abTestConfig.intent_workflow_id,
          version: variantVersion,
          versionType,
        })
      : undefined;

  if (isFetchingAbTestConfigsSummary || isWinnerFetching) {
    return <LoadingCard />;
  }

  const hasWinner = winnerData?.winner;
  const isCompleted =
    abTestConfig?.status === 'completed' || abTestConfig?.status === 'canceled';
  const isDraw = isCompleted && !hasWinner;

  return (
    <Box
      sx={{
        ...(isDraw
          ? {
              backgroundColor: theme.palette.colors.slate[100],
              border: `1px solid ${theme.palette.colors.slate[200]}`,
            }
          : {
              backgroundImage:
                'linear-gradient(135deg, #FFB393 0%, #ABD5FA 50%, #12A6B4 100%)',
              backgroundOrigin: 'border-box',
              border: '1px solid transparent',
              boxShadow: 'inset 0 100vw white',
            }),
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '370px',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          ...(isDraw
            ? { background: 'transparent' }
            : {
                background:
                  'linear-gradient(135deg, rgba(255, 179, 147, 0.15) 0%, rgba(171, 213, 250, 0.15) 50%, rgba(18, 166, 180, 0.15) 100%)',
              }),
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '16px',
          justifyContent: 'flex-start',
          padding: '12px 24px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              background: isDraw
                ? theme.palette.colors.red[200]
                : theme.palette.colors.yellow[500],
              borderRadius: '20px',
              display: 'flex',
              height: '40px',
              justifyContent: 'center',
              width: '40px',
            }}
          >
            {isDraw ? <IconScale size={25} /> : <IconTrophy size={25} />}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            {abTestConfigSummary?.intent_name && (
              <Box>
                <Trans
                  components={[
                    <Typography key={0} variant='font16'>
                      {''}
                    </Typography>,
                    <Typography key={1} variant='font16Bold'>
                      {''}
                    </Typography>,
                  ]}
                  i18nKey='winnerCard.testingLine'
                  ns='agent-ab-test'
                  values={{ variantName: abTestConfigSummary.intent_name }}
                />
              </Box>
            )}
            {abTestConfig?.status === 'running' ? (
              <Box>
                <Trans
                  components={[
                    <Typography key={0} variant='font16'>
                      {''}
                    </Typography>,
                    <Typography key={1} variant='font16Bold'>
                      {''}
                    </Typography>,
                  ]}
                  i18nKey='winnerCard.winnerLine'
                  ns='agent-ab-test'
                  values={{ value: t('winnerCard.resultsPending') }}
                />
              </Box>
            ) : winnerData?.winner ? (
              <Box>
                <Trans
                  components={[
                    <Typography key={0} variant='font16'>
                      {''}
                    </Typography>,
                    <Typography key={1} variant='font16Bold'>
                      {''}
                    </Typography>,
                  ]}
                  i18nKey='winnerCard.winnerLine'
                  ns='agent-ab-test'
                  values={{ value: winnerData.winner.name }}
                />
              </Box>
            ) : (
              <Typography variant='font16Bold'>
                {t('winnerCard.noClearWinner')}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            height: '100%',
            justifyContent: 'flex-start',
          }}
        >
          {winnerData?.variants.map((variant, index) => {
            // Map winner response variant to config variant to get version info.
            // winnerData.variants is ordered [Winner, Loser, ...], but charts
            // color variants by their canonical position (Control first), so
            // resolve the chip color from the config index — not the loop index.
            const configVariantIndex =
              abTestConfig?.variants?.findIndex(
                (cv, cvIndex) =>
                  (cv.name || VARIANT_DEFAULT_NAMES[cvIndex]) === variant.name,
              ) ?? -1;
            const configVariant =
              configVariantIndex >= 0
                ? abTestConfig?.variants?.[configVariantIndex]
                : undefined;
            const colorIndex =
              configVariantIndex >= 0 ? configVariantIndex : index;
            const editUrl = configVariant
              ? getVariantEditUrl(
                  configVariant.intent_workflow_version,
                  configVariant.version_type,
                )
              : undefined;

            return (
              <Box
                component={editUrl ? 'a' : 'div'}
                href={editUrl}
                key={variant.name}
                rel={editUrl ? 'noopener noreferrer' : undefined}
                sx={{
                  '&:hover .variant-name': editUrl
                    ? { textDecoration: 'underline' }
                    : undefined,
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  backgroundColor:
                    isCompleted && winnerData.winner && index === 0
                      ? theme.palette.background.paper
                      : undefined,
                  border: `1px solid ${theme.palette.colors.slate[300]}`,
                  borderRadius: '6px',
                  color: 'inherit',
                  cursor: editUrl ? 'pointer' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  justifyContent: 'center',
                  padding: '8px 12px',
                  textDecoration: 'none',
                }}
                target={editUrl ? '_blank' : undefined}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <Chip
                    color={theme.palette.colors.white}
                    fontSize='12px'
                    label={variant.name || VARIANT_DEFAULT_NAMES[index]}
                    size='small'
                    sx={{
                      backgroundColor: theme.palette.chart[colorIndex],
                      fontWeight: 600,
                    }}
                    variant='filled'
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box className='variant-name' component='span'>
                      <Typography variant='font14Bold'>
                        {variant.version_name}
                      </Typography>
                    </Box>
                  </Box>
                  {isCompleted && winnerData.winner && index === 0 && (
                    <Box
                      sx={{
                        alignItems: 'center',
                        background: theme.palette.colors.yellow[400],
                        borderRadius: '4px',
                        display: 'flex',
                        gap: '2px',
                        padding: '4px 8px',
                      }}
                    >
                      <IconTrophy size={14} />{' '}
                      <Typography variant='font11'>
                        {t('winnerCard.winnerBadge')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant='font12'>
                  {variant.metrics
                    .map(
                      metric =>
                        `${metric.name} ${!variant.is_control && metric.value > 0 ? '+' : ''}${formatNumberWithCommas(
                          metric.value,
                        )}${metric.suffix}`,
                    )
                    .join(', ')}
                </Typography>
              </Box>
            );
          })}
          {winnerData?.winner && (
            <Box sx={{ '& > div': { width: '100%' }, alignSelf: 'stretch' }}>
              <Tooltip
                tooltipContent={publishDisabledTooltip}
                {...(!isPublishDisabled && { open: false })}
              >
                <Box sx={{ width: '100%' }}>
                  <Button
                    disabled={isPublishDisabled}
                    fullWidth
                    onClick={() => setIsPublishDialogOpen(true)}
                    variant='main'
                  >
                    {t('winnerCard.publishButton')}
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
      {winnerData?.winner && (
        <PublishVersionDialog
          intentWorkflowId={abTestConfig?.intent_workflow_id ?? undefined}
          mode='publish'
          onClose={() => setIsPublishDialogOpen(false)}
          open={isPublishDialogOpen}
          versionData={winnerVersion}
        />
      )}
    </Box>
  );
}
